import { Languages } from '@wicadu/langscope'
import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { type Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Gestionar pagos en pedidos',
    seo_description: 'Aprende cómo añadir, actualizar o eliminar pagos manuales en pedidos en wicadu. Guía paso a paso para pedidos y retiros anticipados.',
    seo_keywords: 'gestionar pagos en wicadu, agregar pagos wicadu, actualizar pagos wicadu, eliminar pagos wicadu, gestión pagos en tienda, cómo gestionar pagos en wicadu, pasos para agregar pagos, cómo eliminar pagos en wicadu, procesar pagos, problemas pagos en wicadu',
    page_id: PagesId.ManagingPaymentsForOrders,
    breadcrumbs: {
      home: 'Inicio',
      orders_home: 'Pedidos',
      payments_management: 'Gestionar pagos en pedidos',
    },
    article: {
      title: 'Gestionar pagos en pedidos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para gestionar <em>pedidos</em> y <em>pagos</em> en el <a href=${process.env.STORES_WEBAPP_HOST}>Asistente de wicadu</a>.`,
        `<strong>Usuarios</strong> con permisos para procesar <em>pagos</em> o <em>actualizar estados de pedidos</em>.`
      ],
      about: {
        title: 'Acerca de gestionar pagos',
        content_type: ContentType.Paragraph,
        content: [
          'En ciertas situaciones, como ventas manuales o retiros anticipados, puede que necesites <strong>gestionar pagos manualmente</strong> en un pedido. Esto ocurre comúnmente cuando los clientes pagan en <strong>efectivo</strong>, por <strong>transferencia bancaria</strong> u otros medios alternativos fuera de <strong>wicadu</strong>.'
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `Puedes gestionar pagos para pedidos en <strong>wicadu</strong> solo si:`,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'El pago fue ingresado manualmente o cumplido mediante retirada anticipada.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `El pedido debe estar en estado <em>Preparado</em>.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Revisa los métodos de pago disponibles y sus estados en el articulo de
                  <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.Spanish)} title="Guia de métodos de pago">métodos de pago</a>.`
                ],
              },
            ],
          },
        ],
      },
      how_to_add: {
        title: 'Agregar un pago',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Agregar un pago en <strong>wicadu</strong> es crucial para registrar transacciones de cualquier pedido en tu tienda, ya sea en línea o gestionado manualmente, asegurando un control preciso de las finanzas y una experiencia fluida para tus clientes.',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Acceder al módulo correspondiente: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Dirígete a la funcionalidad de <em>Ventas</em> o <em>Compras</em> en el <strong>Asistente de wicadu</strong>, según el tipo de pedido en el que desees agregar el pago.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Seleccionar el pedido: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'En el grupo de <em>"Preparados"</em> selecciona el pedido a actualizar.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Iniciar gestion de pagos: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'En la sección <em>Abonos</em>, selecciona el botón <strong>Añadir manualmente</strong>. Esto te permitirar agregar abonos por un monto parcial o total del pedido.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Nota:</strong> El estado del pedido debe ser <em>Preparado</em>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Ingresar datos de pago: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Completa el formulario con los campos: <em>Proveedor</em> (por defecto, Control Interno), <em>Método de pago</em>, <em>Estado</em>, <em>Monto</em>, y opcionalmente <em>Fecha estimada de confirmación</em> y <em>Comprobante</em>. Consulta la
                  <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.Spanish)} title="Guía de métodos de pago">Guía de métodos de pago</a> para más detalles.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirmar pago: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Haz clic en <strong>Guardar</strong> para registrar el pago y actualizar el saldo del pedido.`,
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Nota:</strong> Los pedidos permiten múltiples pagos con diferentes métodos de pago hasta cubrir el monto total. Una vez pagado el total, el botón <strong>Añadir manualmente</strong> desaparecerá.',
                    ],
                  },
                ],
              },
            ],
          }
        ]
      },
      how_to_update: {
        title: 'Actualizar un pago',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Actualizar un pago en <strong>wicadu</strong> es fundamental para corregir o completar información de transacciones en los pedidos de tu tienda, garantizando registros financieros precisos y una gestión eficiente. Esta acción permite ajustar detalles como el estado del pago o comprobantes, manteniendo la transparencia en la administración de tus órdenes.',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Acceder al módulo correspondiente: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Dirígete a la funcionalidad de <em>Ventas</em> o <em>Compras</em> en el <strong>Asistente de wicadu</strong>, según el tipo de pedido en el que desees actualizar el pago.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Seleccionar el pedido: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'En el grupo de <em>"Preparados"</em> selecciona el pedido que contiene el pago a actualizar.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Iniciar gestion del pago: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'En la sección <em>Abonos</em>, selecciona el pago que deseas actualizar, una vez dirigido a los detalles del pago, selecciona el boton <strong>Actualizar actividad</strong>.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Nota:</strong> El estado del pedido debe estar en estado <em>Preparado</em> y el pago en estado <em>En proceso</em>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Ingresar datos de pago a actualizar: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Modifica los campos permitidos: <em>Estado</em>, <em>Fecha estimada de confirmación</em> o <em>Comprobante</em>. Consulta la
                  <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.Spanish)} title="Guía de métodos de pago">Guía de métodos de pago</a> para más detalles.`
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirma la actualización: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Haz clic en <strong>Guardar</strong> para actualizar el pago, que se reflejarán de inmediato en el pedido y las finanzas de la tienda`,
                    ],
                  },
                ],
              },
            ],
          }
        ],
      },
      how_to_delete: {
        title: 'Eliminar o anular un pago',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Eliminar o anular un pago en <strong>wicadu</strong> es esencial para corregir errores en las transacciones de cualquier pedido, asegurando que los saldos y las finanzas de tu tienda sean precisos y confiables. Esta acción permite mantener un control claro de los registros financieros.',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Selecciona el pedido en el módulo correspondiente: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Dirígete a la funcionalidad de <em>Ventas</em> o <em>Compras</em> en el <strong>Asistente de wicadu</strong> y, en la sección de pedidos <em>Preparados</em>, elige el pedido que contiene el pago a eliminar.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Inicia la anulación del pago: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'En la sección <em>Abonos</em>, selecciona el pago y elige una de estas opciones:',
                    ],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          'Haz clic en <strong>Editar</strong>, luego en el botón rojo al costado izquierdo y confirma con el botón al costado derecho.'
                        ],
                      },
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          'Accede a los detalles del pago y haz clic en <strong>Anular</strong>.'
                        ],
                      },
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Nota:</strong> El estado del pedido debe estar en estado <em>Preparado</em> y el pago en estado <em>En proceso</em>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirma la anulación:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirma la acción para eliminar el pago. Los cambios se reflejarán de inmediato en el pedido y las finanzas de la tienda',
                    ],
                  },

                ],
              },
            ],
          }
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Tras <strong>gestionar un pago</strong> en el pedido, considera estas acciones como tu siguientes pasos a seguir:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Marcar el pedido como completado: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Una vez cubierto el monto total, marca el pedido como <em>Entregado</em> para mantener tu historial organizado.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Notificar al cliente o proveedor: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Informa a las partes involucradas sobre los ajustes realizados en relacion a los pagos del pedido para mantener una comunicación clara.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      "<strong>Nota:</strong> Si el pedido fue realizado a través de la tienda en línea (<strong>creada con wicadu</strong>), el cliente será notificado automáticamente.",
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verificar los cambios en las finanzas: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Revisa la sección <em>Finanzas</em> en el <a href=${process.env.STORES_WEBAPP_HOST}>Asistente de wicadu</a> para confirmar que los pagos, actualizaciones o anulaciones se reflejen correctamente.`
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>No veo el botón "Añadir manualmente" o "Actualizar actividad"</strong>: Asegúrate de que el pedido esté en estado <em>Preparado</em> y no haya sido procesado a través de la tienda online.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El pago no se refleja en el pedido o las finanzas:</strong> Verifica que hiciste clic en <em>Guardar</em> tras añadir o actualizar el pago. Actualiza la página o revisa la sección de <em>Finanzas</em>'
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>No puedo anular un pago:</strong> Confirma que el pago está en estado <em>En proceso</em> y el pedido en <em>Preparado</em>. Si el pago está confirmado, contacta al equipo de
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.Spanish)} title="soporte de wicadu">soporte de wicadu</a> para asistencia.`
            ],
          },
        ],
      },
    }
  },
  en: {
    seo_title: 'Managing payments for orders',
    seo_description: 'Learn how to add, edit, or remove payments on orders in wicadu. Step-by-step guide for orders and early withdrawals',
    seo_keywords: 'manage payments wicadu, add payments wicadu, update payments wicadu, delete payments wicadu, payment management, how to manage payments in wicadu, steps to add payments, how to delete payments in wicadu, process payments, payment issues in wicadu',
    page_id: PagesId.ManagingPaymentsForOrders,
    breadcrumbs: {
      home: 'Home',
      orders_home: 'Orders',
      payments_management: 'Managing payments for orders',
    },
    article: {
      title: 'Managing payments for orders',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to manage <em>orders</em> and <em>payments</em> in the <a href=${process.env.STORES_WEBAPP_HOST}>wicadu Assistant</a>.`,
        `<strong>Users</strong> with permissions to process <em>payments</em> or <em>update order statuses</em>.`
      ],
      about: {
        title: 'About managing payments',
        content_type: ContentType.Paragraph,
        content: [
          "In certain situations, such as manual orders or early pickups, you may need to <strong>manage payments manually</strong> for your store's orders, especially when customers pay with <strong>cash</strong>, <strong>bank transfer</strong>, or other alternative methods outside of <strong>wicadu</strong>."
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'You can manage payments for orders in <strong>wicadu</strong> only if:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'The payment was added manually or fulfilled through early pickup.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'The order is in <em>Prepared</em> status.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Review available payment methods and their statuses in the
                  <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.English)} title="wicadu payment methods guide">payment methods</a>.`
                ],
              },
            ],
          },
        ],
      },
      how_to_add: {
        title: 'Add a Payment',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Adding a payment in <strong>wicadu</strong> is critical for recording transactions for any order in your store, whether online or manually managed, ensuring accurate financial tracking and a seamless customer experience.'
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Access the relevant module: '],

                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Navigate to the <em>Sales</em> or <em>Purchases</em> functionality in the <strong>wicadu Assistant</strong>, depending on the type of order you wish to add the payment'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Select the order: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'From the <b>In progress</b> section, click the order in the <em>Prepared</em> state where you want to update its payments.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Start payment management: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'In the <em>Payments</em> section, click <strong>Add Manually</strong> to record a partial or full payment.'
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Note:</strong> The order must be in <em>Prepared</em> status.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Enter payment details: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Complete the form with the fields: <em>Provider</em> (default is Internal Control), <em>Payment Method</em>, <em>Status</em>, <em>Amount</em>, and optionally <em>Estimated Confirmation Date</em> and <em>Receipt</em>. See the
                      <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.English)} title="wicadu payment methods guide">payment methods guide</a> for more details.`
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirm the payment: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      "Click <em>Save</em> to record the payment and update the order's balance."
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Note:</strong> Orders support multiple payments with different payment methods until the full amount is paid. Once the full amount is paid, the <strong>Add Manually</strong> button will disappear.'
                    ],
                  },
                ],
              },
            ],
          }
        ]
      },
      how_to_update: {
        title: 'Update a Payment',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              "Updating a payment in <strong>wicadu</strong> is vital for correcting or completing transaction details for your store's orders, ensuring precise financial records and efficient order management. This action allows you to adjust payment statuses or receipts, maintaining transparency in your store's operations."
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Access the relevant module: '],

                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Navigate to the <em>Sales</em> or <em>Purchases</em> functionality in the <strong>wicadu Assistant</strong>, depending on the type of order you wish to add the payment'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Select the order: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'From the <b>In progress</b> section, click the order in the <em>Prepared</em> state where you want to update its payments.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Start payment management: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'In the <em>Payments</em> section, select the payment you want to modify and click <strong>Update Activity</strong>.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Note:</strong> The order must be in <em>Prepared</em> status and the payment in <em>In Process</em> status.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Update payment details: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Modify the allowed fields: <em>Status</em>, <em>Estimated Confirmation Date</em>, or <em>Receipt</em>. See the
                      <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.English)} title="wicadu payment methods guide">Payment methods guide</a> for more details.`
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirm the update: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Click <em>Save</em> to record the payment changes, which will instantly update the order and store finances.'
                    ],
                  },
                ],
              },
            ],
          }
        ],
      },
      how_to_delete: {
        title: 'Delete or Cancel a Payment',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              "Deleting or canceling a payment in <strong>wicadu</strong> is essential for correcting transaction errors in your store's orders, ensuring accurate order balances and reliable financial records. This action helps maintain clear and trustworthy financial tracking."
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Select the order in the relevant module: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Navigate to the <em>Sales</em> or <em>Purchases</em> functionality in the <strong>wicadu Assistant</strong> and, in the <em>Prepared</em> orders section, choose the order containing the payment to delete'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Start payment cancellation: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'In the <em>Payments</em> section, select the payment and choose one of these options:'
                    ],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          'Click <strong>Edit</strong>, then the red button on the left and confirm with the button on the right'
                        ],
                      },
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          'Access the payment details and click <strong>Cancel</strong>'
                        ],
                      },
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Note:</strong> The order must be in <em>Prepared</em> status and the payment in <em>In Process</em> status.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirm the cancellation:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirm the action to delete the payment. Changes will instantly update the order and store finances',
                    ],
                  },

                ],
              },
            ],
          }
        ],
      },
      next_steps: {
        title: 'Next Steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'After <strong>managing a payment</strong> for an order, consider these actions:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Mark the order as completed: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Once the full amount is paid, mark the order as <em>Completed</em> to keep your order history organized.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Notify clients or suppliers: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Inform clients or suppliers about payment changes to maintain clear communication.'
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      "<strong>Note:</strong> If the order was placed through the store's website (<strong>powered by wicadu</strong>), the customer will be notified automatically.",
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verify changes in finances: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Check the <em>Finances</em> section in the <a href=${process.env.STORES_WEBAPP_HOST}>wicadu Assistant</a> to confirm that payments, updates, or cancellations are correctly reflected.`
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>I don't see the "Add Manually" or "Update Activity" button</strong>: Ensure the order is in <em>Prepared</em> status and was not processed through the <strong>wicadu</strong> online store.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              "<strong>The payment isn't reflected in the order or finances</strong>: Confirm you clicked <em>Save</em> after adding or updating the payment. Refresh the page or check the <em>Finances</em>."
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>I can't cancel a payment:</strong> Verify the payment is in <em>In Process</em> status and the order is in <em>Prepared</em> status. If the payment is confirmed, contact
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.English)} title="wicadu Support">wicadu Support</a> team for assistance.`
            ],
          },
        ],
      },
    }
  },
  fr: {
    seo_title: 'Gérer les paiements des commandes',
    seo_description:
      'Découvrez comment ajouter, modifier et suivre les paiements associés à vos commandes dans wicadu.',
    seo_keywords:
      'paiements commande, suivre paiements, ajouter paiement, modifier paiement, commande wicadu',
    page_id: PagesId.ManagingPaymentsForOrders,
    breadcrumbs: {
      home: 'Accueil',
      orders_home: 'Commandes',
      payments_management: 'Gérer les paiements des commandes',
    },
    article: {
      title: 'Gérer les paiements des commandes',
      intended: [
        `<strong>Propriétaires</strong>, <strong>administrateurs</strong> et équipes qui gèrent les <em>encaissements</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        '<strong>Vendeurs</strong> ou équipes opérationnelles autorisés à enregistrer ou mettre à jour les paiements des commandes.',
      ],
      about: {
        title: 'À propos des paiements de commande',
        content_type: ContentType.Paragraph,
        content: [
          'Le suivi des <strong>paiements de commande</strong> permet de relier précisément chaque <strong>vente</strong> ou <strong>achat</strong> à son encaissement réel dans <strong>wicadu</strong>. Une bonne gestion des paiements améliore la visibilité financière, renforce la qualité du suivi comptable et limite les erreurs dans la gestion des <strong>commandes</strong>.',
          'En enregistrant correctement les paiements, leurs statuts et leurs justificatifs, vous pouvez mieux contrôler les soldes, détecter plus vite les écarts et garder une meilleure cohérence entre <strong>commandes</strong>, <strong>documents</strong> et <strong>modes de paiement</strong>.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Vous pouvez gérer les paiements des commandes dans <strong>wicadu</strong> uniquement si :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              { content_type: ContentType.Paragraph, content: ['Le paiement a été ajouté manuellement ou traité dans le cadre d’un retrait anticipé.'] },
              { content_type: ContentType.Paragraph, content: ['La commande est à l’état <em>Préparé</em>.'] },
              { content_type: ContentType.Paragraph, content: ['Consultez les <a href='+getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.French)+' title=\"Guide des modes de paiement dans wicadu\">modes de paiement</a> disponibles et leurs statuts.'] },
            ],
          },
        ],
      },
      how_to_add: {
        title: 'Comment ajouter un paiement',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ajouter un paiement dans <strong>wicadu</strong> est essentiel pour enregistrer les transactions de n’importe quelle commande de votre boutique, qu’il s’agisse d’une commande en ligne ou gérée manuellement, afin d’assurer un suivi financier précis et une expérience fluide pour le client.',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Accéder au module concerné : '] },
                  { content_type: ContentType.Paragraph, content: ['Accédez à la fonctionnalité <em>Ventes</em> ou <em>Achats</em> dans l’<strong>assistant wicadu</strong>, selon le type de commande sur laquelle vous souhaitez ajouter le paiement.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la commande : '] },
                  { content_type: ContentType.Paragraph, content: ['Dans la section <b>En cours</b>, cliquez sur la commande à l’état <em>Préparé</em> pour laquelle vous souhaitez mettre à jour les paiements.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Commencer la gestion du paiement : '] },
                  { content_type: ContentType.Paragraph, content: ['Dans la section <em>Paiements</em>, cliquez sur <strong>Ajouter manuellement</strong> afin d’enregistrer un paiement partiel ou total.'] },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque :</strong> La commande doit être à l’état <em>Préparé</em>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Saisir les détails du paiement : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Remplissez le formulaire avec les champs suivants : <em>Fournisseur</em> (par défaut, Contrôle interne), <em>Mode de paiement</em>, <em>Statut</em>, <em>Montant</em> et, si nécessaire, <em>Date estimée de confirmation</em> et <em>Reçu</em>. Consultez le
                      <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.French)} title="Guide des modes de paiement dans wicadu">guide des modes de paiement</a> pour plus de détails.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Confirmer le paiement : '] },
                  { content_type: ContentType.Paragraph, content: ['Cliquez sur <em>Enregistrer</em> pour comptabiliser le paiement et mettre à jour le solde de la commande.'] },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque :</strong> Les commandes acceptent plusieurs paiements avec différents modes de paiement jusqu’à ce que le montant total soit couvert. Une fois le montant total payé, le bouton <strong>Ajouter manuellement</strong> disparaît.',
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      how_to_update: {
        title: 'Comment mettre à jour un paiement',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Mettre à jour un paiement dans <strong>wicadu</strong> est important pour corriger ou compléter les informations liées aux transactions de vos commandes, afin de garantir des registres financiers fiables et une gestion plus efficace des commandes. Cette action permet notamment d’ajuster le statut du paiement ou les justificatifs associés.',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Accéder au module concerné : '] },
                  { content_type: ContentType.Paragraph, content: ['Accédez à la fonctionnalité <em>Ventes</em> ou <em>Achats</em> dans l’<strong>assistant wicadu</strong>, selon le type de commande sur laquelle vous souhaitez modifier le paiement.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la commande : '] },
                  { content_type: ContentType.Paragraph, content: ['Dans la section <b>En cours</b>, cliquez sur la commande à l’état <em>Préparé</em> qui contient le paiement à mettre à jour.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Commencer la gestion du paiement : '] },
                  { content_type: ContentType.Paragraph, content: ['Dans la section <em>Paiements</em>, sélectionnez le paiement que vous souhaitez modifier puis cliquez sur <strong>Mettre à jour l’activité</strong>.'] },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque :</strong> La commande doit être à l’état <em>Préparé</em> et le paiement à l’état <em>En cours de traitement</em>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Mettre à jour les détails du paiement : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Modifiez les champs autorisés : <em>Statut</em>, <em>Date estimée de confirmation</em> ou <em>Reçu</em>. Consultez le
                      <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.French)} title="Guide des modes de paiement dans wicadu">guide des modes de paiement</a> pour plus de détails.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Confirmer la mise à jour : '] },
                  { content_type: ContentType.Paragraph, content: ['Cliquez sur <em>Enregistrer</em> pour sauvegarder les changements, qui seront immédiatement reflétés dans la commande et dans les finances de la boutique.'] },
                ],
              },
            ],
          },
        ],
      },
      how_to_delete: {
        title: 'Comment supprimer un paiement',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Supprimer ou annuler un paiement dans <strong>wicadu</strong> est utile pour corriger des erreurs de transaction dans vos commandes, afin de garantir des soldes exacts et des registres financiers fiables. Cette action vous aide à conserver un suivi clair et cohérent de vos opérations.',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la commande dans le module concerné : '] },
                  { content_type: ContentType.Paragraph, content: ['Accédez à la fonctionnalité <em>Ventes</em> ou <em>Achats</em> dans l’<strong>assistant wicadu</strong> et, dans la section des commandes à l’état <em>Préparé</em>, choisissez la commande qui contient le paiement à supprimer.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Commencer l’annulation du paiement : '] },
                  { content_type: ContentType.Paragraph, content: ['Dans la section <em>Paiements</em>, sélectionnez le paiement puis choisissez l’une des options suivantes :'] },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          'Cliquez sur <strong>Modifier</strong>, puis sur le bouton rouge à gauche et confirmez avec le bouton à droite.',
                        ],
                      },
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          'Accédez aux détails du paiement puis cliquez sur <strong>Annuler</strong>.',
                        ],
                      },
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque :</strong> La commande doit être à l’état <em>Préparé</em> et le paiement à l’état <em>En cours de traitement</em>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Confirmer l’annulation : '] },
                  { content_type: ContentType.Paragraph, content: ['Confirmez l’action pour supprimer le paiement. Les changements seront immédiatement reflétés dans la commande et dans les finances de la boutique.'] },
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Après avoir <strong>géré un paiement</strong> sur une commande, vous pouvez poursuivre avec les actions suivantes :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Marquer la commande comme terminée : '] },
                  { content_type: ContentType.Paragraph, content: ['Une fois le montant total payé, marquez la commande comme <em>Terminée</em> afin de garder votre historique de commandes bien organisé.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Informer les clients ou les fournisseurs : '] },
                  { content_type: ContentType.Paragraph, content: ['Informez les clients ou les fournisseurs des changements apportés aux paiements afin de maintenir une communication claire.'] },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque :</strong> Si la commande a été passée via le site de la boutique (<strong>créé avec wicadu</strong>), le client sera automatiquement informé.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier les changements dans les finances : '] },
                  { content_type: ContentType.Paragraph, content: [`Consultez la section <em>Finances</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a> pour confirmer que les paiements, les mises à jour ou les annulations sont correctement reflétés.`] },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Je ne vois pas le bouton "Ajouter manuellement" ou "Mettre à jour l’activité"</strong> : assurez-vous que la commande est à l’état <em>Préparé</em> et qu’elle n’a pas été traitée via la boutique en ligne <strong>wicadu</strong>.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Le paiement ne se reflète pas dans la commande ou dans les finances</strong> : vérifiez que vous avez bien cliqué sur <em>Enregistrer</em> après avoir ajouté ou mis à jour le paiement. Actualisez la page ou consultez la section <em>Finances</em>.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Je ne peux pas annuler un paiement</strong> : confirmez que le paiement est à l’état <em>En cours de traitement</em> et que la commande est à l’état <em>Préparé</em>. Si le paiement est déjà confirmé, contactez le
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.French)} title="support wicadu">support wicadu</a> pour obtenir de l’aide.`,
            ],
          },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
